// web/src/types/client.ts - Client management types

import type { User } from './user';
import type { Project } from './project';
import type { Scan } from './scan';
import type { ScanResult } from './finding';

export interface Client {
  id: number;
  name: string;
  description?: string;
  website?: string;
  industry?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  logo?: string;
  active: boolean;
  notes?: string;
  // Extended profile fields
  team_size?: string;           // e.g., "1-10", "11-50", "51-200", "201-500", "500+"
  infrastructure_type?: string; // e.g., "On-Premise", "Cloud", "Hybrid", "Multi-Cloud"
  company_size?: string;        // e.g., "SME", "Enterprise", "Startup"
  tech_stack?: string;          // Primary technologies used
  compliance_requirements?: string; // Required compliance frameworks
  // Agent permissions
  agents_enabled: boolean;
  max_agents: number;
  agent_downloadable: boolean;
  // Client-manageable settings
  ai_features_enabled: boolean;
  carry_forward_enabled?: boolean;
  omit_info_findings?: boolean;
  notifications_enabled: boolean;
  auto_report_generation: boolean;
  preferred_language: string;
  preferred_report_format: string;
  data_retention_days: number;
  allow_api_access: boolean;
  // Timestamps
  created_at: string;
  updated_at: string;
  contacts?: ClientContact[];
  projects?: Project[];
  contact_count?: number;
  project_count?: number;
  schedule_count?: number;
  scan_count?: number;
  total_logins?: number;
  // Domain restrictions (admin-only configurable)
  base_domain?: string;
  additional_domains?: string;
  enforce_domain_scope?: boolean;
  // Network ownership overrides (recon IP-block classification)
  own_asns?: string;
  own_netblocks?: string;
  own_org_aliases?: string;
  // Remediation targets (days)
  remediation_critical_days?: number;
  remediation_high_days?: number;
  remediation_medium_days?: number;
  remediation_low_days?: number;
}

export interface ClientContact {
  id: number;
  client_id: number;
  client?: Client;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  department?: string;
  is_primary: boolean;
  active: boolean;
  notes?: string;
  notify_on_report: boolean;
  notify_on_critical: boolean;
  notify_weekly_digest: boolean;
  notify_on_exposure: boolean;
  notify_on_breach: boolean;
  vacation_mode: boolean;
  user_id?: number;
  user?: User;
  login_count?: number;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectClientContact {
  id: number;
  project_id: number;
  client_contact_id: number;
  client_contact?: ClientContact;
  created_at: string;
}

// Scan Report & Notification interfaces
export interface ScanReport {
  id: number;
  scan_id: number;
  scan?: Scan;
  project_id: number;
  project?: Project;
  title: string;
  summary?: string;
  new_assets_count: number;
  new_assets?: string;
  new_findings?: string;
  resolved_findings?: string;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  total_findings: number;
  trend_data?: string;
  previous_scan_id?: number;
  delta_summary?: string;
  status: 'pending' | 'reviewed' | 'sent' | 'archived';
  reviewed_by_id?: number;
  reviewed_by?: User;
  reviewed_at?: string;
  review_notes?: string;
  false_positive_ids?: string;
  excluded_result_ids?: string;
  sent_to_client_at?: string;
  sent_to_contacts?: string;
  client_report_url?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationLog {
  id: number;
  type: 'scan_complete' | 'report_ready' | 'critical_finding' | 'digest';
  recipient_type: 'user' | 'client_contact';
  recipient_id: number;
  recipient_email: string;
  subject: string;
  body?: string;
  status: 'pending' | 'sent' | 'failed' | 'bounced';
  sent_at?: string;
  error?: string;
  scan_id?: number;
  project_id?: number;
  scan_report_id?: number;
  metadata?: string;
  created_at: string;
}

export interface NotificationStats {
  total_sent: number;
  total_pending: number;
  total_failed: number;
  sent_today: number;
  sent_this_week: number;
  by_type: Record<string, number>;
}

// Alerting System interfaces
export interface PersonalAlertingSettings {
  // User info
  user_id: number;
  email: string;
  full_name: string;
  vacation_mode: boolean;

  // Email settings
  email_enabled: boolean;

  // Scan reports
  email_scan_completed: boolean;
  email_scan_failed: boolean;
  email_new_vulns: boolean;
  min_severity: 'critical' | 'high' | 'medium' | 'low';

  // Exposure monitoring
  email_exposure_new_port: boolean;
  email_exposure_new_host: boolean;
  email_exposure_cert_expiry: boolean;
  email_exposure_service: boolean;

  // Breach monitoring
  email_breach_new_data: boolean;
  email_breach_critical: boolean;

  // Tickets
  email_ticket_new: boolean;
  email_ticket_reply: boolean;
  email_ticket_assigned: boolean;
  email_ticket_resolved: boolean;
  email_ticket_deadline: boolean;

  // System alerts
  email_system_alerts: boolean;
  email_agent_status: boolean;
  email_security_alerts: boolean;

  // Weekly digest
  digest_enabled: boolean;
  digest_frequency: 'daily' | 'weekly';
  digest_day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  digest_include_summary: boolean;
  digest_include_resolved: boolean;
  digest_include_expiring: boolean;

  // In-app notifications
  in_app_enabled: boolean;
  in_app_ticket_new: boolean;
  in_app_ticket_reply: boolean;
  in_app_system_alerts: boolean;
  in_app_scan_completed: boolean;

  // Push notifications - per-user Pushover user key
  pushover_user_key: string;

  // Feature availability (for UI to gray out unavailable sections)
  has_exposure_monitoring: boolean;
  has_breach_monitoring: boolean;
}

export interface CompanyAlertingOverview {
  // Company settings
  client_id: number;
  client_name: string;
  notifications_enabled: boolean;
  auto_report_generation: boolean;
  preferred_language: string;
  has_exposure_monitoring: boolean;
  has_breach_monitoring: boolean;

  // Summary
  total_users: number;
  users_receiving_alerts: number;
  users_on_vacation: number;
  no_alerts_warning: boolean;

  // User list
  users: CompanyUserAlertingSummary[];
}

export interface CompanyUserAlertingSummary {
  user_id?: number;
  contact_id?: number;
  email: string;
  full_name: string;
  is_primary: boolean;
  active: boolean;
  vacation_mode: boolean;
  alerts_enabled: boolean;
  can_toggle_alerts: boolean; // Primary contacts cannot toggle off subscription alerts
  role: string;
}

export interface ProjectRecipient {
  id: number;
  type: 'contact' | 'user';
  email: string;
  name: string;
  is_primary: boolean;
  vacation_mode: boolean;
}

export interface ProjectRecipientsResponse {
  recipients: ProjectRecipient[];
  allow_custom: boolean;
  project_id: number;
  client_id?: number;
  client_name?: string;
}

// Form types for client management
export interface CreateClientRequest {
  name: string;
  description?: string;
  website?: string;
  industry?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  logo?: string;
  notes?: string;
}

export interface ClientFormData {
  name: string;
  description: string;
  website: string;
  industry: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  logo: string;
  active: boolean;
  // Extended profile fields
  team_size?: string;
  infrastructure_type?: string;
  company_size?: string;
  tech_stack?: string;
  compliance_requirements?: string;
  // Agent permissions
  agents_enabled: boolean;
  max_agents: number;
  // Network ownership overrides (recon IP-block classification)
  own_asns?: string;
  own_netblocks?: string;
  own_org_aliases?: string;
}

export interface ContactFormData {
  client_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  department: string;
  is_primary: boolean;
  notify_on_report: boolean;
  active: boolean;
}

export interface CreateContactRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  department?: string;
  is_primary?: boolean;
  notify_on_report?: boolean;
  notify_on_critical?: boolean;
  notify_weekly_digest?: boolean;
  notes?: string;
}

export interface ReviewReportRequest {
  review_notes?: string;
  false_positive_ids?: number[];
  excluded_result_ids?: number[];
}

export interface SendReportRequest {
  contact_ids?: number[];
  message?: string;
}

export interface UpdateProjectClientRequest {
  client_id?: number | null;
  project_manager_id?: number | null;
  notify_manager_on_scan?: boolean;
  notify_client_on_scan?: boolean;
}

// Constants
export const CLIENT_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Government',
  'Non-profit',
  'Legal',
  'Real Estate',
  'Hospitality',
  'Media',
  'Telecommunications',
  'Energy',
  'Transportation',
  'Other',
] as const;

export const CONTACT_ROLES = [
  'CEO',
  'CTO',
  'CISO',
  'IT Manager',
  'Security Manager',
  'System Administrator',
  'Network Administrator',
  'Developer',
  'Project Manager',
  'Compliance Officer',
  'Other',
] as const;

export const REPORT_STATUSES = ['pending', 'reviewed', 'sent', 'archived'] as const;
export const NOTIFICATION_TYPES = ['scan_complete', 'report_ready', 'critical_finding', 'digest'] as const;
export const NOTIFICATION_STATUSES = ['pending', 'sent', 'failed', 'bounced'] as const;

// Type guards
export const isReportStatus = (status: string): status is ScanReport['status'] => {
  return REPORT_STATUSES.includes(status as any);
};

export const isNotificationType = (type: string): type is NotificationLog['type'] => {
  return NOTIFICATION_TYPES.includes(type as any);
};

export const isNotificationStatus = (status: string): status is NotificationLog['status'] => {
  return NOTIFICATION_STATUSES.includes(status as any);
};
